import { type AiRubricCreateSuccess, APIError, isResponseError, Logger, type AiRubricCreateInput } from '@terros/common'
import { getApiUrl, HeadersContext, HTTPStatuses, useBackend } from '../api'
import { getRequestOptions } from '../api/getRequestOptions'
import { useCallback, useContext } from 'react'

const logger = new Logger('create-rubric')

export function useCreateRubric(): (input: AiRubricCreateInput) => Promise<AiRubricCreateSuccess> {
  const backend = useBackend()
  const additionalHeaders = useContext(HeadersContext)

  return useCallback(
    async (input: AiRubricCreateInput): Promise<AiRubricCreateSuccess> => {
      const fullURL = getApiUrl(backend.getStage()) + '/ai/rubric/create'
      const checksum = await calculateChecksum(input)

      try {
        const options = await getRequestOptions({ backend, fullURL, input, additionalHeaders })

        const response = await fetch(fullURL, {
          ...options,
          headers: { ...options.headers, 'x-amz-content-sha256': checksum },
        })
        const data = await response.json()

        logger.verbose('response', response.status, data)

        if (!response.ok) {
          const statusText = HTTPStatuses[response.status] ?? 'Unknown server error'
          throw new APIError({
            type: 'error',
            error: 'Server',
            message: statusText,
            retry: false,
          })
        }

        if (isResponseError(data)) throw new APIError(data)

        return data as AiRubricCreateSuccess
      } catch (error) {
        if (error instanceof TypeError) {
          // Handle network errors (e.g., no internet, server unreachable, timeout)
          logger.error(`Network error for ${fullURL}:`, error)
          throw new APIError({ type: 'error', error: 'Network' })
        }

        logger.warn(`Request failed for ${fullURL}:`, error)
        if (error instanceof APIError) throw error
        throw new APIError({
          type: 'error',
          error: 'Server',
          message: error instanceof Error ? error.message : String(error),
          retry: false,
        })
      } finally {
        logger.verbose('Request completed for', fullURL)
      }
    },
    [backend, additionalHeaders]
  )
}

const calculateChecksum = async (input: Object): Promise<string> => {
  const body = new TextEncoder().encode(JSON.stringify(input))
  const binChecksum = await crypto.subtle.digest('SHA-256', body)
  return arrayBufferToHex(binChecksum)
}

function arrayBufferToHex(buffer: ArrayBuffer): string {
  const view = new Uint8Array(buffer)
  const hex: string[] = []

  for (let i = 0; i < view.length; i++) hex.push(view[i].toString(16).padStart(2, '0'))

  return hex.join('')
}
